import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import RaceResultsViewer from '../components/RaceResultsViewer';
import { HotlapFilterResponse, HotlapRow } from '../types';
import { Search } from 'lucide-react';
import { SortDirection, getSortIndicator, sortRows } from '../utils/sort';
import './HotlapPage.css';

const HotlapPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<HotlapFilterResponse>({ tracks: [], drivers: [], cars: [], sessionTypes: [] });
  const [hotlaps, setHotlaps] = useState<HotlapRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{ key: 'track' | 'driver' | 'car' | 'bestLap' | 'date' | 'session'; direction: SortDirection }>({ key: 'date', direction: 'desc' });
  const [viewingSession, setViewingSession] = useState<{ sessionId: string | number; carModel: string } | null>(null);

  const track = searchParams.get('track') ?? '';
  const trackLayout = searchParams.get('layout') ?? '';
  const driver = searchParams.get('driver') ?? '';
  const car = searchParams.get('car') ?? '';
  const sessionType = searchParams.get('sessionType') ?? '';
  const startDate = searchParams.get('startDate') ?? '';
  const endDate = searchParams.get('endDate') ?? '';

  const setFilter = (key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      return next;
    }, { replace: true });
  };

  const setTrackLayout = (value: string) => setFilter('layout', value);
  const setDriver = (value: string) => setFilter('driver', value);
  const setCar = (value: string) => setFilter('car', value);
  const setSessionType = (value: string) => setFilter('sessionType', value);
  const setStartDate = (value: string) => setFilter('startDate', value);
  const setEndDate = (value: string) => setFilter('endDate', value);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const res = await api.getHotlapFilters();
        setFilters(res.data);
      } catch (err) {
        console.error('Failed to load hotlap filters:', err);
      }
    };
    loadFilters();
  }, []);

  const loadHotlaps = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getHotlaps({
        trackName: track || undefined,
        trackLayoutName: trackLayout || undefined,
        driverName: driver || undefined,
        carModel: car || undefined,
        sessionType: sessionType || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      // Filter out invalid laps (bestLapMs of 0)
      const allHotlaps: HotlapRow[] = res.data.hotlaps || [];
      setHotlaps(allHotlaps.filter((row) => row.bestLapMs > 0));
    } catch (err) {
      console.error('Failed to load hotlaps:', err);
      setError('Unable to load hotlap data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHotlaps();
  }, [track, trackLayout, driver, car, sessionType, startDate, endDate]);

  const driverOptions = useMemo(
    () => filters.drivers.filter((driverName) => driverName.toLowerCase().includes(driver.toLowerCase())),
    [filters.drivers, driver]
  );

  const carOptions = useMemo(
    () => filters.cars.filter((carName) => carName.toLowerCase().includes(car.toLowerCase())),
    [filters.cars, car]
  );

  const formatTime = (ms: number | null) => {
    if (ms === null) return '-';
    const totalSeconds = Math.floor(ms / 1000);
    const milliseconds = ms % 1000;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes > 0) {
      return `${minutes}:${String(seconds).padStart(2, '0')}.${String(milliseconds).padStart(3, '0')}`;
    }
    return `${seconds}.${String(milliseconds).padStart(3, '0')}`;
  };

  const handleDriverClick = (driverId?: number) => {
    if (driverId) {
      navigate(`/drivers/${driverId}`);
    }
  };

  const handleSort = (key: 'track' | 'driver' | 'car' | 'bestLap' | 'date' | 'session') => {
    setSortConfig((current) => {
      if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const sortedHotlaps = useMemo(() => sortRows(hotlaps, sortConfig.direction, (row) => {
    switch (sortConfig.key) {
      case 'track':
        return `${row.trackName} ${row.trackLayoutName}`;
      case 'driver':
        return row.firstName && row.lastName ? `${row.firstName} ${row.lastName}` : row.driverName;
      case 'car':
        return row.carModel;
      case 'bestLap':
        return row.bestLapMs ?? 0;
      case 'date':
        return row.sessionDate;
      case 'session':
        return row.sessionType;
      default:
        return '';
    }
  }), [hotlaps, sortConfig]);

  return (
    <div>
      <div className="page-header">
        <h1>Hotlap Records</h1>
        <p>Search hotlap performance with optional date range filters.</p>
      </div>

      <div className="section-card" style={{ marginBottom: '24px' }}>
        <div className="form-row">
          <label>
            Track
            <select
              value={track ? `${track}|||${trackLayout}` : ''}
              onChange={(e) => {
                const value = e.target.value;
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  if (!value) {
                    next.delete('track');
                    next.delete('layout');
                  } else {
                    const [selectedTrack, selectedLayout] = value.split('|||');
                    next.set('track', selectedTrack);
                    if (selectedLayout) {
                      next.set('layout', selectedLayout);
                    } else {
                      next.delete('layout');
                    }
                  }
                  return next;
                }, { replace: true });
              }}
            >
              <option value="">All tracks</option>
              {filters.tracks.map((trackItem) => (
                <option
                  key={`${trackItem.trackName}-${trackItem.trackLayoutName}`}
                  value={`${trackItem.trackName}|||${trackItem.trackLayoutName}`}
                >
                  {trackItem.trackName} {trackItem.trackLayoutName}
                </option>
              ))}
            </select>
          </label>

          <label>
            Session type
            <select value={sessionType} onChange={(e) => setSessionType(e.target.value)}>
              <option value="">All session types</option>
              {filters.sessionTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </label>

          <label>
            Driver
            <input
              type="text"
              list="driver-options"
              placeholder="Search driver"
              value={driver}
              onChange={(e) => setDriver(e.target.value)}
            />
            <datalist id="driver-options">
              {driverOptions.map((driverName) => (
                <option key={driverName} value={driverName} />
              ))}
            </datalist>
          </label>

          <label>
            Car
            <input
              type="text"
              list="car-options"
              placeholder="Search car"
              value={car}
              onChange={(e) => setCar(e.target.value)}
            />
            <datalist id="car-options">
              {carOptions.map((carName) => (
                <option key={carName} value={carName} />
              ))}
            </datalist>
          </label>
        </div>

        <div className="form-row" style={{ alignItems: 'end' }}>
          <label>
            Start date
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
          <label>
            End date
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </label>
          <div style={{ alignSelf: 'end' }}>
            <button className="primary-button" onClick={loadHotlaps} disabled={loading}>
              <Search size={16} /> Refresh
            </button>
          </div>
        </div>
      </div>

      {error && <div className="empty-state">{error}</div>}

      <div className="section-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
          <h2 style={{ margin: 0 }}>{hotlaps.length} hotlap entries</h2>
        </div>

        <div className="hotlap-sort-bar">
          <span className="hotlap-sort-label">Sort by</span>
          <select
            className="hotlap-sort-select"
            value={`${sortConfig.key}:${sortConfig.direction}`}
            onChange={(e) => {
              const [k, d] = e.target.value.split(':') as ['track' | 'driver' | 'car' | 'bestLap' | 'date' | 'session', SortDirection];
              setSortConfig({ key: k, direction: d });
            }}
          >
            <option value="date:desc">Date (newest first)</option>
            <option value="date:asc">Date (oldest first)</option>
            <option value="bestLap:asc">Best Lap (fastest)</option>
            <option value="bestLap:desc">Best Lap (slowest)</option>
            <option value="track:asc">Track (A → Z)</option>
            <option value="track:desc">Track (Z → A)</option>
            <option value="driver:asc">Driver (A → Z)</option>
            <option value="driver:desc">Driver (Z → A)</option>
            <option value="car:asc">Car (A → Z)</option>
            <option value="car:desc">Car (Z → A)</option>
          </select>
        </div>

        <div className="table-wrapper">
          <table className="hotlap-table">
            <thead>
              <tr>
                <th><button type="button" onClick={() => handleSort('track')} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Track <span aria-hidden="true">{getSortIndicator(sortConfig.key, 'track', sortConfig.direction)}</span></button></th>
                <th><button type="button" onClick={() => handleSort('driver')} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Driver <span aria-hidden="true">{getSortIndicator(sortConfig.key, 'driver', sortConfig.direction)}</span></button></th>
                <th><button type="button" onClick={() => handleSort('car')} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Car <span aria-hidden="true">{getSortIndicator(sortConfig.key, 'car', sortConfig.direction)}</span></button></th>
                <th><button type="button" onClick={() => handleSort('bestLap')} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Best Lap <span aria-hidden="true">{getSortIndicator(sortConfig.key, 'bestLap', sortConfig.direction)}</span></button></th>
                <th><button type="button" onClick={() => handleSort('date')} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Date <span aria-hidden="true">{getSortIndicator(sortConfig.key, 'date', sortConfig.direction)}</span></button></th>
                <th><button type="button" onClick={() => handleSort('session')} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Session <span aria-hidden="true">{getSortIndicator(sortConfig.key, 'session', sortConfig.direction)}</span></button></th>
              </tr>
            </thead>
            <tbody>
              {sortedHotlaps.map((row) => (
                <tr
                  key={`${row.sessionId}-${row.driverName}-${row.carModel}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setViewingSession({ sessionId: row.sessionId, carModel: row.carModel })}
                >
                  <td data-label="Track"><span>{row.trackName} {row.trackLayoutName}</span></td>
                  <td data-label="Driver">
                    {row.driverId ? (
                      <button
                        type="button"
                        className="hotlap-driver-btn"
                        onClick={(e) => { e.stopPropagation(); handleDriverClick(row.driverId); }}
                      >
                        {row.firstName && row.lastName
                          ? `${row.firstName} ${row.lastName}`
                          : row.driverName}
                      </button>
                    ) : (
                      <span>
                        {row.firstName && row.lastName
                          ? `${row.firstName} ${row.lastName}`
                          : row.driverName}
                      </span>
                    )}
                  </td>
                  <td data-label="Car"><span>{row.carModel}</span></td>
                  <td data-label="Best Lap"><span>{formatTime(row.bestLapMs)}</span></td>
                  <td data-label="Date"><span>{row.sessionDate ? new Date(row.sessionDate).toLocaleDateString() : 'Unknown'}</span></td>
                  <td data-label="Session"><span>{row.sessionType}</span></td>
                </tr>
              ))}
              {hotlaps.length === 0 && (
                <tr>
                  <td colSpan={6} className="empty-state">No hotlaps match the current filter.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {viewingSession !== null && (
        <RaceResultsViewer
          sessionId={Number(viewingSession.sessionId)}
          onClose={() => setViewingSession(null)}
          fetchResult={async () => api.getResultsSession(String(viewingSession.sessionId))}
        />
      )}
    </div>
  );
};

export default HotlapPage;
